import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Check, X, Trash2, Star, Filter,
  Eye, EyeOff, User, Mail, Phone, Clock, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReview, setSelectedReview] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [statusFilter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/reviews/admin/all${params}`);
      setReviews(response.data.reviews || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (error) {
      toast.error('Failed to fetch reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (reviewId, status) => {
    try {
      await axios.put(`${API}/reviews/admin/${reviewId}`, {
        status,
        admin_notes: adminNotes
      });
      
      toast.success(`Review ${status} successfully`);
      setSelectedReview(null);
      setAdminNotes('');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review permanently?')) return;

    try {
      await axios.delete(`${API}/reviews/admin/${reviewId}`);
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const StarRating = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={16}
          className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
        />
      ))}
    </div>
  );

  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPageName = (pageId) => {
    const pages = {
      outreach: 'Living Fruit Outreach',
      pantry: 'Food Pantry',
      animal_rescue: 'Smallest Souls',
      programs: 'Programs',
      events: 'Events',
      general: 'General'
    };
    return pages[pageId] || pageId;
  };

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif text-charcoal">Review Management</h1>
        <p className="text-charcoal/60">Approve or reject community reviews before they go public</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Pending', count: counts.pending, color: 'bg-yellow-50 border-yellow-200 text-yellow-700', filter: 'pending' },
          { label: 'Approved', count: counts.approved, color: 'bg-green-50 border-green-200 text-green-700', filter: 'approved' },
          { label: 'Rejected', count: counts.rejected, color: 'bg-red-50 border-red-200 text-red-700', filter: 'rejected' },
          { label: 'Total', count: counts.total, color: 'bg-gray-50 border-gray-200 text-gray-700', filter: '' }
        ].map((stat) => (
          <button
            key={stat.label}
            onClick={() => setStatusFilter(stat.filter)}
            className={`p-4 rounded-xl border text-left transition-all ${stat.color} ${
              statusFilter === stat.filter ? 'ring-2 ring-offset-2 ring-green-500' : ''
            }`}
          >
            <div className="text-2xl font-bold">{stat.count}</div>
            <div className="text-sm opacity-70">{stat.label}</div>
          </button>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 border border-gold/10 flex items-center gap-4">
        <Filter size={18} className="text-charcoal/40" />
        <span className="text-charcoal/60 text-sm">Filter:</span>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', ''].map((status) => (
            <button
              key={status || 'all'}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                statusFilter === status
                  ? 'bg-green-700 text-white'
                  : 'bg-cream text-charcoal/70 hover:bg-gold/20'
              }`}
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-green-700" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gold/10">
          <MessageSquare size={48} className="mx-auto text-charcoal/20 mb-4" />
          <p className="text-charcoal/60">No {statusFilter || ''} reviews found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review.review_id}
              className="bg-white rounded-xl border border-gold/10 overflow-hidden"
            >
              {/* Review Header */}
              <div className="p-4 border-b border-gold/10 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <User size={20} className="text-green-700" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-charcoal">{review.name}</span>
                      {review.display_anonymous && (
                        <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          <EyeOff size={12} />
                          Shows as Anonymous
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-charcoal/50">
                      <span className="flex items-center gap-1">
                        <Mail size={12} />
                        {review.email}
                      </span>
                      {review.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} />
                          {review.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={review.status} />
                  <span className="text-xs text-charcoal/50 bg-cream px-2 py-1 rounded">
                    {getPageName(review.page)}
                  </span>
                </div>
              </div>

              {/* Review Content */}
              <div className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <StarRating rating={review.rating} />
                  <span className="text-xs text-charcoal/50 flex items-center gap-1">
                    <Clock size={12} />
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="font-medium text-charcoal mb-2">{review.title}</h3>
                <p className="text-charcoal/70">{review.content}</p>
                
                {review.admin_notes && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-xs text-yellow-800 font-medium mb-1">Admin Notes:</p>
                    <p className="text-sm text-yellow-700">{review.admin_notes}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 bg-cream/50 border-t border-gold/10 flex flex-wrap gap-2">
                {review.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => handleUpdateStatus(review.review_id, 'approved')}
                      className="bg-green-700 hover:bg-green-800 text-white"
                      size="sm"
                    >
                      <Check size={16} className="mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => setSelectedReview(review)}
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      size="sm"
                    >
                      <X size={16} className="mr-1" />
                      Reject
                    </Button>
                  </>
                )}
                {review.status === 'approved' && (
                  <Button
                    onClick={() => handleUpdateStatus(review.review_id, 'pending')}
                    variant="outline"
                    size="sm"
                  >
                    Move to Pending
                  </Button>
                )}
                {review.status === 'rejected' && (
                  <Button
                    onClick={() => handleUpdateStatus(review.review_id, 'approved')}
                    className="bg-green-700 hover:bg-green-800 text-white"
                    size="sm"
                  >
                    <Check size={16} className="mr-1" />
                    Approve
                  </Button>
                )}
                <Button
                  onClick={() => handleDelete(review.review_id)}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 ml-auto"
                  size="sm"
                >
                  <Trash2 size={16} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rejection Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h2 className="text-xl font-serif text-charcoal mb-4">Reject Review</h2>
            
            <div className="mb-4 p-3 bg-cream rounded-lg">
              <p className="text-sm text-charcoal/70">
                <strong>{selectedReview.name}</strong>: "{selectedReview.title}"
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-charcoal mb-2">
                Reason for rejection (optional)
              </label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about why this review was rejected..."
                rows={3}
                className="bg-cream"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedReview(null);
                  setAdminNotes('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleUpdateStatus(selectedReview.review_id, 'rejected')}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Reject Review
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
